/* Copyright (c) 2026 - Svayam Infoware Pvt. Ltd. All rights reserved. */
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';

export interface AdminResource {
  id: string | number;
  resourceName: string;
  label?: string;
  description?: string;
  sortOrder?: number;
  status?: string;
  permissionCodes?: string[];
  permissionCount?: number;
  nonAdminGrantCount?: number;
  grantedRoleNames?: string[];
  canDelete?: boolean;
}

@Component({
  selector: 'app-resources',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './resources.component.html',
  styleUrl: './resources.component.scss',
})
export class ResourcesComponent implements OnInit {
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);

  readonly items = signal<AdminResource[]>([]);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly message = signal<string | null>(null);
  readonly createOpen = signal(false);
  readonly editOpen = signal(false);
  readonly deleteOpen = signal(false);
  readonly editing = signal<AdminResource | null>(null);
  readonly deleting = signal<AdminResource | null>(null);

  formLabel = '';
  formCode = '';
  formDescription = '';

  canManage(): boolean {
    return this.rbac.canModule('roles');
  }

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  async reload(): Promise<void> {
    if (!this.canManage()) {
      this.message.set('Only Admin can manage RBAC resources.');
      return;
    }
    this.loading.set(true);
    this.message.set(null);
    try {
      this.items.set(await this.ops.listResources());
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Failed to load resources');
    } finally {
      this.loading.set(false);
    }
  }

  openCreate(): void {
    if (!this.canManage()) return;
    this.formLabel = '';
    this.formCode = '';
    this.formDescription = '';
    this.createOpen.set(true);
  }

  closeCreate(force = false): void {
    if (this.busy() && !force) return;
    this.createOpen.set(false);
  }

  openEdit(row: AdminResource): void {
    this.editing.set(row);
    this.formLabel = row.label || row.resourceName;
    this.formCode = row.resourceName;
    this.formDescription = row.description || '';
    this.editOpen.set(true);
  }

  closeEdit(force = false): void {
    if (this.busy() && !force) return;
    this.editOpen.set(false);
    this.editing.set(null);
  }

  openDelete(row: AdminResource): void {
    this.deleting.set(row);
    this.deleteOpen.set(true);
  }

  closeDelete(force = false): void {
    if (this.busy() && !force) return;
    this.deleteOpen.set(false);
    this.deleting.set(null);
  }

  async create(): Promise<void> {
    if (!this.formCode.trim()) {
      this.message.set('Resource code is required.');
      return;
    }
    this.busy.set(true);
    try {
      const created = await this.ops.createResource({
        resourceName: this.formCode,
        label: this.formLabel || undefined,
        description: this.formDescription || undefined,
      });
      this.closeCreate(true);
      this.message.set(
        `Resource “${created.resourceName}” created with ${created.permissionCodes?.length || 0} permission codes. It appears on Roles & permissions immediately.`,
      );
      await this.reload();
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Create resource failed');
    } finally {
      this.busy.set(false);
    }
  }

  async saveEdit(): Promise<void> {
    const row = this.editing();
    if (!row) return;
    this.busy.set(true);
    try {
      await this.ops.updateResource(row.id, {
        label: this.formLabel,
        description: this.formDescription,
      });
      this.closeEdit(true);
      this.message.set(`Resource “${row.resourceName}” updated.`);
      await this.reload();
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Update failed');
    } finally {
      this.busy.set(false);
    }
  }

  async confirmDelete(): Promise<void> {
    const row = this.deleting();
    if (!row) return;
    this.busy.set(true);
    try {
      await this.ops.deleteResource(row.id);
      this.closeDelete(true);
      this.message.set(`Resource “${row.resourceName}” deleted.`);
      await this.reload();
    } catch (err) {
      this.message.set(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      this.busy.set(false);
    }
  }

  codesPreview(row: AdminResource): string {
    return (row.permissionCodes || []).join(', ');
  }

  /** Template-safe preview of CODE_ACCESS from the create form. */
  previewAccessCode(): string {
    const code = String(this.formCode || 'CODE')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_')
      .replace(/[^A-Z0-9_]/g, '');
    return `${code || 'CODE'}_ACCESS`;
  }
}
